// Cart summary component showing totals and rules validation

import {
  calculateCartQuantity,
  calculateCartTotal,
  formatPrice,
  type CartItem,
} from "@/lib/queries/products";

interface SelectionRule {
  minQuantity?: number | null;
  maxQuantity?: number | null;
  maxPrice?: number | null;
  label?: string | null;
  description?: string | null;
  settings?: { showDescription?: boolean } | null;
}

interface CartSummaryProps {
  cart: CartItem[];
  currencyCode?: string;
  showPrice?: boolean;
  selectionRule?: SelectionRule | null;
  productConditionsMet?: boolean;
}

export function CartSummary({
  cart,
  currencyCode = "USD",
  showPrice = true,
  selectionRule,
}: CartSummaryProps) {
  const totalItems = calculateCartQuantity(cart);
  const totalPrice = calculateCartTotal(cart);

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Items</span>
          <span className="font-medium text-foreground">{totalItems}</span>
        </div>

        {selectionRule?.minQuantity && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Minimum Required</span>
            <span className="font-medium text-foreground">
              {selectionRule.minQuantity}
            </span>
          </div>
        )}

        {selectionRule?.maxQuantity && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Maximum Allowed</span>
            <span className="font-medium text-foreground">
              {selectionRule.maxQuantity}
            </span>
          </div>
        )}

        {showPrice && (
          <div className="border-t border-border pt-2 mt-2">
            <div className="flex justify-between">
              <span className="font-medium text-foreground">Total</span>
              <span className="font-semibold text-foreground">
                {formatPrice(totalPrice, currencyCode)}
              </span>
            </div>

            {selectionRule?.maxPrice && (
              <div className="flex justify-between text-sm mt-1">
                <span className="text-muted-foreground">Limit</span>
                <span className="text-muted-foreground">
                  {formatPrice(selectionRule.maxPrice, currencyCode)}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
